import { Match, Switch, on, onCleanup, Show, createEffect, onMount, For, createSignal } from "solid-js";
import * as config from './config';
import socket from "./socket";
import { ProjectView } from "./Project";
import { useUserContext } from "./UserContext";
import { Loader } from "./Components";

export default (props) => {

  const [projects, setProjects] = createSignal(null);
  const [showCreateModal, setShowCreateModal] = createSignal(false);
  const [newProjectName, setNewProjectName] = createSignal('');
  const [newProjectDesc, setNewProjectDesc] = createSignal('');

  const [userStore, setUserStore, userActions] = useUserContext();

  const currentProjectId = () => {
    const m = userStore.page.match(/^\/projects\/(\d+)/);
    return m ? parseInt(m[1]) : null;
  };

  onMount(() => {
    socket.emit('list-projects');

    socket.on('project-list', (data) => {
      setProjects(data);
    });
    socket.on('new-project', (data) => {
      setProjects([...(projects() || []), data]);
    });
    socket.on('deleted-project', (project_id) => {
      setProjects((projects() || []).filter((p) => p.id !== project_id));
      if(currentProjectId() === project_id) {
        userActions.setPage('/');
      }
    });
  });

  createEffect(() => {
    const pId = currentProjectId();
    if (pId && !userStore.openedProject[pId]) {
      socket.emit('open-project', { id: pId });
    }
  });

  const handleCreateProject = (e) => {
    e.preventDefault();
    const name = newProjectName().trim();
    if (!name) return;
    socket.emit('create-project', { 
      name, 
      description: newProjectDesc().trim() 
    });
    setNewProjectName('');
    setNewProjectDesc('');
    setShowCreateModal(false);
  };

  return (
    <div>
      <Show when={currentProjectId()}>
        {(pId) => <ProjectView project_id={pId()} />}
      </Show>

      <Show when={!currentProjectId()}>
        <Show when={projects() === null}>
          <Loader message="Chargement des projets..." />
        </Show>

        <Show when={projects() !== null}>
        <div class="d-flex flex-wrap justify-content-between align-items-center mb-4 pb-2 border-bottom">
          <div>
            <h1 class="h3 fw-bold text-dark mb-1">Mes projets</h1>
            <p class="text-muted small mb-0">Gérez vos grilles d'évaluation et suivez les notes de vos étudiants.</p>
          </div>
          <button
            type="button"
            class="btn btn-primary d-inline-flex align-items-center gap-2 shadow-sm"
            onClick={() => setShowCreateModal(true)}
          >
            <i class="bi bi-plus-lg"></i>
            <span>Nouveau projet</span>
          </button>
        </div>

        <Show when={showCreateModal()}>
          <div class="modal show d-block" style="background: rgba(15, 23, 42, 0.4); backdrop-filter: blur(4px);" tabindex="-1">
            <div class="modal-dialog modal-dialog-centered">
              <div class="modal-content shadow-lg border-0 rounded-4">
                <form onSubmit={handleCreateProject}>
                  <div class="modal-header border-bottom-0 pb-0">
                    <h5 class="modal-title fw-bold">Créer un nouveau projet</h5>
                    <button
                      type="button"
                      class="btn-close"
                      onClick={() => setShowCreateModal(false)}
                      aria-label="Fermer"
                    ></button>
                  </div>
                  <div class="modal-body py-4">
                    <div class="mb-3">
                      <label class="form-label fw-semibold">Nom du projet <span class="text-danger">*</span></label>
                      <input
                        type="text"
                        class="form-control"
                        placeholder="Ex: Soutenances M1 Informatique 2026"
                        required
                        autofocus
                        value={newProjectName()}
                        onInput={(e) => setNewProjectName(e.currentTarget.value)}
                      />
                    </div>
                    <div class="mb-2">
                      <label class="form-label fw-semibold">Description (optionnelle)</label>
                      <textarea
                        class="form-control"
                        rows={3}
                        placeholder="Ex: Évaluation de la soutenance et du rapport technique"
                        value={newProjectDesc()}
                        onInput={(e) => setNewProjectDesc(e.currentTarget.value)}
                      ></textarea>
                    </div>
                  </div>
                  <div class="modal-footer border-top-0 pt-0">
                    <button
                      type="button"
                      class="btn btn-light"
                      onClick={() => setShowCreateModal(false)}
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      class="btn btn-primary"
                      disabled={!newProjectName().trim()}
                    >
                      Créer le projet
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </Show>

        <Show when={projects().length === 0}>
          <div class="empty-state my-4">
            <div class="empty-icon">
              <i class="bi bi-folder-plus"></i>
            </div>
            <h4>Aucun projet pour le moment</h4>
            <p>Créez votre premier projet d'évaluation pour commencer à noter vos étudiants et partager avec vos collègues.</p>
            <button
              type="button"
              class="btn btn-primary shadow-sm"
              onClick={() => setShowCreateModal(true)}
            >
              <i class="bi bi-plus-lg me-1"></i>
              Créer un projet
            </button>
          </div>
        </Show>

        <div class="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4">
          <For each={projects()}>
            {(p) => (
              <div class="col">
                <div class="project-card h-100">
                  <div class="project-card-header">
                    <div class="project-icon">
                      <i class="bi bi-journal-bookmark-fill"></i>
                    </div>
                    <span class="project-id-badge">#{p.id}</span>
                  </div>
                  <div class="card-body">
                    <div>
                      <h5 class="project-title">{p.name}</h5>
                      <p class="project-desc">
                        {p.description || <span class="text-muted fst-italic">Aucune description</span>}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        socket.emit('open-project', { id: p.id });
                        userActions.setPage('/projects/' + p.id);
                      }}
                      class="btn btn-outline-primary w-100 d-inline-flex align-items-center justify-content-center gap-2 mt-2 fw-medium"
                    >
                      <span>Ouvrir l'évaluation</span>
                      <i class="bi bi-arrow-right"></i>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </For>
        </div>
        </Show>
      </Show>
    </div>
  );
};



